import { zodResolver } from "@hookform/resolvers/zod"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Check, KeyRound, Plus, Trash2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { type Resolver, useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { ApiToken } from "@/api/generated/ApiToken"
import type { CreatedApiToken } from "@/api/generated/CreatedApiToken"
import {
  API_TOKEN_SCOPES,
  type ApiTokenScope,
  useApiTokens,
  useCreateApiToken,
  useRevokeApiToken,
} from "@/api/queries/api-tokens"
import { useSession } from "@/api/queries/session"
import { CodeBlock } from "@/components/app/code-block"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { FieldShell, SelectField, SwitchField, TextField } from "@/components/app/fields"
import { FormRoot, FormRootError, SubmitButton } from "@/components/app/form"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { SecretReveal } from "@/components/app/secret-reveal"
import { EmptyState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { DropdownMenuItem } from "@/components/ui/dropdown-menu"
import { McpAccessPanel } from "@/features/settings/mcp-access"
import i18n from "@/i18n"
import { toDate } from "@/lib/format"
import { can } from "@/lib/permissions"
import { requireCap } from "@/lib/route-guards"
import { cn } from "@/lib/utils"
import { z } from "@/lib/zod"

interface TokensSearch {
  create?: boolean
}

export const Route = createFileRoute("/_app/settings/api-tokens")({
  beforeLoad: requireCap("management"),
  staticData: { titleKey: "nav.items.apiTokens" },
  validateSearch: (s: Record<string, unknown>): TokensSearch => ({
    create: s.create === true || s.create === "true" ? true : undefined,
  }),
  component: ApiTokensPage,
})

function isExpired(token: ApiToken) {
  const d = toDate(token.expires_at)
  return !!d && d.getTime() < Date.now()
}

function ApiTokensPage() {
  const { t } = useTranslation()
  const session = useSession()
  const tokens = useApiTokens()
  const revoke = useRevokeApiToken()
  const confirm = useConfirm()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [createOpen, setCreateOpen] = useState(false)
  const open = createOpen || !!search.create
  const setOpen = (o: boolean) => {
    setCreateOpen(o)
    if (!o && search.create) void navigate({ search: {}, replace: true })
  }

  const confirmRevoke = (token: ApiToken) =>
    confirm({
      title: t("apiTokens.revokeConfirm.title", { name: token.name }),
      description: t("apiTokens.revokeConfirm.description"),
      confirmLabel: t("apiTokens.revoke"),
      destructive: true,
      onConfirm: () => revoke.mutateAsync(token),
    })

  const columns = useMemo(
    () =>
      createColumns<ApiToken>((c) => [
        c.accessor("name", {
          header: t("apiTokens.columns.name"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="min-w-0">
              <div className="truncate font-medium">{row.original.name}</div>
              <code className="text-muted-foreground font-mono text-xs">{row.original.prefix}…</code>
            </div>
          ),
        }),
        c.accessor((tk) => tk.scopes.join(" "), {
          id: "scopes",
          header: t("apiTokens.columns.scopes"),
          meta: { label: t("apiTokens.columns.scopes") },
          enableSorting: false,
          cell: ({ row }) => (
            <div className="flex flex-wrap gap-1">
              {row.original.scopes.map((s) => (
                <StatusBadge
                  key={s}
                  tone={s === "*" ? "warning" : s === "mcp" ? "info" : "neutral"}
                  className="font-mono"
                >
                  {s === "*" ? t("apiTokens.scopes.all.short") : s}
                </StatusBadge>
              ))}
            </div>
          ),
        }),
        c.accessor((tk) => tk.username ?? "", {
          id: "owner",
          header: t("apiTokens.columns.owner"),
          meta: { label: t("apiTokens.columns.owner") },
          cell: ({ row }) =>
            row.original.user_id == null ? (
              <StatusBadge tone="info">{t("apiTokens.service")}</StatusBadge>
            ) : (
              <span className="text-muted-foreground">@{row.original.username}</span>
            ),
        }),
        c.accessor("last_used_at", {
          header: t("apiTokens.columns.lastUsed"),
          sortFn: "datetime",
          sortUndefined: "last",
          cell: ({ getValue }) => (
            <RelativeTime
              value={getValue()}
              fallback={t("apiTokens.neverUsed")}
              className="text-muted-foreground"
            />
          ),
        }),
        c.accessor("expires_at", {
          header: t("apiTokens.columns.expires"),
          sortFn: "datetime",
          sortUndefined: "last",
          cell: ({ row }) =>
            isExpired(row.original) ? (
              <StatusBadge tone="danger">{t("apiTokens.expired")}</StatusBadge>
            ) : row.original.expires_at ? (
              <RelativeTime value={row.original.expires_at} className="text-muted-foreground" />
            ) : (
              <span className="text-muted-foreground">{t("apiTokens.noExpiry")}</span>
            ),
        }),
        c.accessor("created_at", {
          header: t("apiTokens.columns.created"),
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
      ]),
    [t],
  )

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("apiTokens.title")}
        description={t("apiTokens.description")}
        icon={KeyRound}
        actions={
          <Button onClick={() => setOpen(true)} data-testid="create-token">
            <Plus />
            {t("apiTokens.create")}
          </Button>
        }
      />
      <DataTable
        label={t("apiTokens.title")}
        data={tokens.data}
        columns={columns}
        getRowId={(tk) => String(tk.id)}
        loading={tokens.isPending}
        error={tokens.error}
        onRetry={() => void tokens.refetch()}
        searchable={(tokens.data?.length ?? 0) > 5}
        initialSorting={[{ id: "created_at", desc: true }]}
        rowActions={(tk) => (
          <DropdownMenuItem variant="destructive" onClick={() => void confirmRevoke(tk)}>
            <Trash2 />
            {t("apiTokens.revoke")}
          </DropdownMenuItem>
        )}
        emptyState={
          <EmptyState
            variant="plain"
            icon={KeyRound}
            title={t("apiTokens.empty.title")}
            description={t("apiTokens.empty.description")}
            action={
              <Button size="sm" onClick={() => setOpen(true)}>
                <Plus />
                {t("apiTokens.create")}
              </Button>
            }
          />
        }
      />
      <div className="text-muted-foreground mt-6 max-w-2xl space-y-2 text-sm">
        <p>{t("apiTokens.usage")}</p>
        <CodeBlock
          code={'curl -H "Authorization: Bearer <token>" \\\n  https://your-host/api/me'}
          language="bash"
        />
      </div>
      {can(session, "platformAdmin") && <McpAccessPanel />}
      <CreateTokenDialog open={open} onOpenChange={setOpen} />
    </PageContainer>
  )
}

const EXPIRY_OPTIONS = ["7", "30", "90", "365", "never"] as const

const schema = z.object({
  name: z.string().trim().min(1).max(100),
  scopes: z.array(z.enum(API_TOKEN_SCOPES)).min(1, { error: () => i18n.t("apiTokens.form.scopesRequired") }),
  service: z.boolean(),
  expiry: z.enum(EXPIRY_OPTIONS),
})
type Values = z.infer<typeof schema>

function ScopePicker({
  value,
  onChange,
}: {
  value: ApiTokenScope[]
  onChange: (v: ApiTokenScope[]) => void
}) {
  const { t } = useTranslation()
  const all = value.includes("*")
  const toggle = (scope: ApiTokenScope, on: boolean) => {
    if (scope === "*") return onChange(on ? ["*"] : [])
    const next = new Set(value.filter((s) => s !== "*"))
    if (on) next.add(scope)
    else next.delete(scope)
    onChange([...next])
  }
  const key = (s: ApiTokenScope) => (s === "*" ? "all" : s === "kb:search" ? "kbSearch" : s)
  return (
    <div className="grid gap-1.5 sm:grid-cols-2">
      {API_TOKEN_SCOPES.map((scope) => {
        const checked = value.includes(scope) || (all && scope !== "*")
        const disabled = all && scope !== "*"
        return (
          // biome-ignore lint/a11y/noLabelWithoutControl: Base UI's Checkbox renders the control (role=checkbox) inside
          <label
            key={scope}
            className={cn(
              "hover:bg-muted/50 flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5 transition-colors",
              checked && "border-primary/30 bg-primary/[0.04]",
              disabled && "cursor-not-allowed opacity-60",
              scope === "*" && "sm:col-span-2",
            )}
          >
            <Checkbox
              checked={checked}
              disabled={disabled}
              onCheckedChange={(v) => toggle(scope, !!v)}
              className="mt-0.5"
            />
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-[0.8125rem] font-medium">
                {t(`apiTokens.scopes.${key(scope)}.label`)}
                <code className="text-muted-foreground font-mono text-[0.6875rem] font-normal">{scope}</code>
              </span>
              <span className="text-muted-foreground block text-xs">
                {t(`apiTokens.scopes.${key(scope)}.description`)}
              </span>
            </span>
          </label>
        )
      })}
    </div>
  )
}

function CreateTokenDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation()
  const create = useCreateApiToken()
  const [created, setCreated] = useState<CreatedApiToken | null>(null)
  const form = useForm<Values>({
    resolver: zodResolver(schema) as Resolver<Values>,
    defaultValues: { name: "", scopes: [], service: false, expiry: "90" },
  })
  useEffect(() => {
    if (open) {
      setCreated(null)
      form.reset({ name: "", scopes: [], service: false, expiry: "90" })
    }
  }, [open, form])
  const scopes = useWatch({ control: form.control, name: "scopes" })

  return (
    <Dialog open={open} onOpenChange={(o) => !form.formState.isSubmitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-xl" showCloseButton={!created}>
        {created ? (
          <div className="grid gap-5">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="bg-success/15 text-success flex size-6 items-center justify-center rounded-full">
                  <Check className="size-3.5" />
                </span>
                {t("apiTokens.createdTitle", { name: created.name })}
              </DialogTitle>
              <DialogDescription>{t("apiTokens.createdDescription")}</DialogDescription>
            </DialogHeader>
            <SecretReveal secret={created.secret} />
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} data-testid="token-done">
                {t("apiTokens.done")}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <FormRoot
            form={form}
            className="grid gap-5"
            onSubmit={async (v) => {
              const token = await create.mutateAsync({
                name: v.name,
                scopes: v.scopes,
                service: v.service || undefined,
                expires_in_days: v.expiry === "never" ? undefined : Number(v.expiry),
              })
              setCreated(token)
            }}
          >
            <DialogHeader>
              <DialogTitle>{t("apiTokens.create")}</DialogTitle>
              <DialogDescription>{t("apiTokens.createDescription")}</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4">
              <TextField
                control={form.control}
                name="name"
                label={t("apiTokens.form.name")}
                placeholder={t("apiTokens.form.namePlaceholder")}
                required
                autoFocus
              />
              <FieldShell
                id="scopes"
                label={t("apiTokens.form.scopes")}
                error={form.formState.errors.scopes}
                required
              >
                <ScopePicker
                  value={scopes}
                  onChange={(v) => form.setValue("scopes", v, { shouldDirty: true, shouldValidate: true })}
                />
              </FieldShell>
              <div className="grid gap-4 sm:grid-cols-2">
                <SelectField
                  control={form.control}
                  name="expiry"
                  label={t("apiTokens.form.expiry")}
                  options={EXPIRY_OPTIONS.map((o) => ({
                    value: o,
                    label:
                      o === "never"
                        ? t("apiTokens.form.never")
                        : t("apiTokens.form.days", { count: Number(o) }),
                  }))}
                />
                <SwitchField
                  control={form.control}
                  name="service"
                  variant="card"
                  label={t("apiTokens.form.service")}
                  description={t("apiTokens.form.serviceHint")}
                />
              </div>
            </div>
            <FormRootError form={form} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("common.cancel")}
              </Button>
              <SubmitButton form={form}>{t("apiTokens.form.submit")}</SubmitButton>
            </DialogFooter>
          </FormRoot>
        )}
      </DialogContent>
    </Dialog>
  )
}
